import { create } from 'zustand';
import { contractsApi } from '../services/contracts-api';
import type { ContractDeal, DealWithFiles } from '../types/contracts';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface ContractsState {
  fromDate: string;
  toDate: string;

  deals: ContractDeal[];
  selectedDeal: DealWithFiles | null;

  reportFrom: string;
  reportTo: string;
  fetchedKey: string | null;

  isLoading: boolean;
  isLoadingFiles: boolean;
  error: string | null;

  searchQuery: string;
  chungTuFilter: string[];

  setDateRange: (from: string, to: string) => void;
  setSearchQuery: (query: string) => void;
  toggleChungTuFilter: (value: string) => void;
  fetchDeals: (force?: boolean) => Promise<void>;
  fetchDealFiles: (dealId: string) => Promise<void>;
  clearSelectedDeal: () => void;
}

const now = new Date();
const lastMonth = subMonths(now, 1);
const defaultFrom = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
const defaultTo = format(endOfMonth(lastMonth), 'yyyy-MM-dd');

export const useContractsStore = create<ContractsState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,

  deals: [],
  selectedDeal: null,

  reportFrom: defaultFrom,
  reportTo: defaultTo,
  fetchedKey: null,

  isLoading: false,
  isLoadingFiles: false,
  error: null,

  searchQuery: '',
  chungTuFilter: [],

  setDateRange: (from, to) => {
    set({ fromDate: from, toDate: to });
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  toggleChungTuFilter: (value) => {
    const current = get().chungTuFilter;
    if (current.includes(value)) {
      set({ chungTuFilter: current.filter(v => v !== value) });
    } else {
      set({ chungTuFilter: [...current, value] });
    }
  },

  fetchDeals: async (force = false) => {
    const { fromDate, toDate, fetchedKey } = get();
    const currentKey = `${fromDate}|${toDate}`;

    if (!force && fetchedKey === currentKey) return;

    set({ isLoading: true, error: null });

    try {
      const deals = await contractsApi.getContractDeals({ from: fromDate, to: toDate });
      set({
        deals,
        isLoading: false,
        fetchedKey: currentKey,
        reportFrom: fromDate,
        reportTo: toDate,
      });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch contract deals', isLoading: false });
    }
  },

  fetchDealFiles: async (dealId: string) => {
    set({ isLoadingFiles: true, error: null });

    try {
      const data = await contractsApi.getDealFiles(dealId);
      set({ selectedDeal: data, isLoadingFiles: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch deal files', isLoadingFiles: false });
    }
  },

  clearSelectedDeal: () => {
    set({ selectedDeal: null });
  },
}));
